import { CardModifier, MapNode, RunAbility, UnlockState, MetaProfile } from '../types';
import { BALANCE } from '../constants';
import { generateMap } from '../map/MapGenerator';
import { Relic } from '../relics/Relic';
import { computeUnlockState } from '../meta/Profile';

export interface RunData {
  playerHp: number;
  playerMaxHp: number;
  deckNumbers: number[];
  modifiers: Map<number, CardModifier>; // number -> modifier attached to that card
  permanentExhaust: number[];
  curses: string[]; // curse IDs, duplicates allowed
  seed: number;
  unlockSnapshot: UnlockState;
  // Map state
  map: MapNode[][];
  currentNodeId: string | null;
  visitedNodeIds: string[];
  act: number;
  // Relics
  relics: Relic[];
  // Persistent abilities
  abilities: RunAbility[];
  // Buffs
  drawBonusBattles: number; // extra draw for N remaining battles
  // Stats
  battlesWon: number;
  elitesDefeated: number;
  bossesDefeated: number;
  combosTriggered: number;
}

const DEFAULT_UNLOCK_SNAPSHOT: UnlockState = {
  maxRarity: 'common',
  eventPack: 1,
  cursePack: 1,
  abilityPack: 1,
};

function cloneUnlockSnapshot(snapshot?: UnlockState): UnlockState {
  if (!snapshot) return { ...DEFAULT_UNLOCK_SNAPSHOT };
  return {
    maxRarity: snapshot.maxRarity,
    eventPack: Math.max(1, snapshot.eventPack),
    cursePack: Math.max(1, snapshot.cursePack),
    abilityPack: Math.max(1, snapshot.abilityPack),
  };
}

export function createNewRun(seed?: number): RunData {
  const s = seed ?? Date.now();
  return {
    playerHp: BALANCE.STARTING_HP,
    playerMaxHp: BALANCE.STARTING_HP,
    deckNumbers: [...BALANCE.STARTING_DECK],
    modifiers: new Map(),
    permanentExhaust: [],
    curses: [],
    seed: s,
    unlockSnapshot: cloneUnlockSnapshot(DEFAULT_UNLOCK_SNAPSHOT),
    map: generateMap(s, 1),
    currentNodeId: null,
    visitedNodeIds: [],
    act: 1,
    relics: [],
    abilities: [],
    drawBonusBattles: 0,
    battlesWon: 0,
    elitesDefeated: 0,
    bossesDefeated: 0,
    combosTriggered: 0,
  };
}

export function createRunFromProfile(profile: MetaProfile, seed?: number): RunData {
  const run = createNewRun(seed);
  run.unlockSnapshot = cloneUnlockSnapshot(computeUnlockState(profile));
  return run;
}

export function normalizeRunData(run: RunData): RunData {
  run.abilities = run.abilities ?? [];
  run.curses = run.curses ?? [];
  run.unlockSnapshot = cloneUnlockSnapshot(run.unlockSnapshot);
  return run;
}

export function addCardToRun(run: RunData, number: number, modifier?: CardModifier): void {
  if (!run.deckNumbers.includes(number)) {
    run.deckNumbers.push(number);
  }
  if (modifier) {
    run.modifiers.set(number, modifier);
  }
}

export function hasAbility(run: RunData, abilityId: string): boolean {
  return run.abilities.some(a => a.id === abilityId);
}

export function addAbility(run: RunData, ability: RunAbility): void {
  if (!hasAbility(run, ability.id)) {
    run.abilities.push(ability);
  }
}

export function addCurse(run: RunData, curseId: string): void {
  run.curses.push(curseId);
}

export function removeCurse(run: RunData, curseIdOrIndex: string | number): string | null {
  if (typeof curseIdOrIndex === 'number') {
    if (curseIdOrIndex < 0 || curseIdOrIndex >= run.curses.length) return null;
    const removed = run.curses[curseIdOrIndex];
    run.curses.splice(curseIdOrIndex, 1);
    return removed;
  }

  const idx = run.curses.indexOf(curseIdOrIndex);
  if (idx === -1) return null;
  const removed = run.curses[idx];
  run.curses.splice(idx, 1);
  return removed;
}

export function getAvailableDeck(run: RunData): number[] {
  return run.deckNumbers.filter(n => !run.permanentExhaust.includes(n));
}

export function getReachableNodes(run: RunData): MapNode[] {
  if (!run.currentNodeId) {
    // Start of run: first row is reachable
    return run.map[0] ?? [];
  }
  // Find current node and return its connections
  for (const row of run.map) {
    const current = row.find(n => n.id === run.currentNodeId);
    if (current) {
      const nextRow = run.map[current.row + 1];
      if (!nextRow) return [];
      return nextRow.filter(n => current.connections.includes(n.id));
    }
  }
  return [];
}

export function hasRelic(run: RunData, relicId: string): boolean {
  return run.relics.some(r => r.id === relicId);
}

export function addRelic(run: RunData, relic: Relic): void {
  if (!hasRelic(run, relic.id)) {
    run.relics.push(relic);
    // Apply immediate effects
    if (relic.id === 'iron_heart') {
      run.playerMaxHp += 5;
      run.playerHp += 5;
    }
  }
}

export function startAct2(run: RunData): void {
  run.act = 2;
  run.map = generateMap(run.seed, 2);
  run.currentNodeId = null;
  // Keep visitedNodeIds for stats but won't collide with new map IDs
}

export function isRunComplete(run: RunData): boolean {
  // Run is complete when the boss node is visited
  const lastRow = run.map[run.map.length - 1];
  if (!lastRow) return false;
  const bossNode = lastRow.find(n => n.type === 'boss');
  return bossNode ? run.visitedNodeIds.includes(bossNode.id) : false;
}
